import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { format, formatDistanceToNow, parseISO, differenceInCalendarDays } from 'date-fns';
import { ChevronDown, ChevronRight, AlertTriangle, ArrowUp, ArrowDown, Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  fetchMyTendersSummary,
  fetchMyTendersTimeline,
  formatStatusUpdatedAt,
  getDealValueDisplay,
  formatDealValueDisplay,
  formatNativeValueHint,
  formatUSDCompact,
  resolveDealId,
} from '@/lib/api/myTenders';
import DealStatusPicker from '@/components/procurement/DealStatusPicker';
import SourceLink from '@/components/procurement/SourceLink';
import { normalizeDeal, getDeadlineHorizon } from '@/utils/procurementDealUtils';
import { getDealCategories } from '@/utils/procurementCategoryUtils';
import { toOfficialCategoryName, getProfileProcurementCategories } from '@/utils/edtechCategoryNames';
import { dealMatchesPrimaryProcurementCategories } from '@/lib/procurementTenantLiveDeals';
import { useTranslation } from '@/components/utils/translations';
import { fetchProductProfiles, fetchFitLatest } from '@/lib/api/fit';

const FIT_NULL_LABELS = new Set(['Insufficient Info', 'Low Evidence']);
const FIT_POLL_MS = 15000;
const FIT_CONCURRENCY = 8;
const PURSUING_COUNTER_STATUSES = new Set(['pursuing', 'submitted', 'won']);

function debounce(fn, ms) {
  let timeoutId;
  const debounced = (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), ms);
  };
  debounced.cancel = () => clearTimeout(timeoutId);
  return debounced;
}

function formatDealTrackingSinceLabel(trackingStartedAt) {
  if (!trackingStartedAt) return 'Deal tracking';
  try {
    const monthKey = String(trackingStartedAt).slice(0, 7);
    const d = parseISO(`${monthKey}-01`);
    return `Deal tracking since ${format(d, 'MMMM yyyy')}`;
  } catch {
    return 'Deal tracking';
  }
}

function countDealsWithStatuses(liveDeals, recentlyExpiredDeals, augment, statuses) {
  const seen = new Set();
  let count = 0;
  for (const d of [...liveDeals, ...recentlyExpiredDeals]) {
    const id = resolveDealId(d);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    if (statuses.has(augment[id]?.status)) count++;
  }
  return count;
}

function formatMoney(value, currency = 'GBP') {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency || 'GBP',
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${currency || ''} ${n.toLocaleString()}`.trim();
  }
}

const FRAMEWORK_CEILING_TOOLTIP =
  'Framework and Dynamic Purchasing System notices publish the total spend across all suppliers for the life of the agreement, so they are shown separately rather than added in.';

function TotalDealValueCard({ counters }) {
  const value = Number(counters?.total_deal_value) || 0;
  const count = Number(counters?.total_deal_count) || 0;
  const unknown = Number(counters?.total_deal_unknown_count) || 0;
  const frameworkValue = Number(counters?.total_deal_framework_value) || 0;
  const frameworkCount = Number(counters?.total_deal_framework_count) || 0;
  const isZero = value === 0 && count === 0;
  const exact = `$${Math.round(value).toLocaleString()}`;

  return (
    <div className="rounded-lg border bg-white p-3">
      <p className="text-xs text-[#5C6B7A]">Total Deal Value</p>
      <p className="text-2xl font-bold text-[#0A1A2F]" title={exact}>
        {formatUSDCompact(value)}
      </p>
      {isZero ? (
        <p className="text-xs text-[#5C6B7A]">
          no strong or moderate fit deals with a published value
        </p>
      ) : (
        <>
          <p className="text-xs text-[#5C6B7A]">
            across {count} strong + moderate fit deals
          </p>
          {frameworkCount > 0 ? (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <p className="text-xs text-[#5C6B7A] cursor-help">
                    + {formatUSDCompact(frameworkValue)} in framework ceilings
                  </p>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs max-w-[260px]">
                  {FRAMEWORK_CEILING_TOOLTIP}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : unknown > 0 ? (
            <p className="text-xs text-[#5C6B7A]">{unknown} with no value published</p>
          ) : null}
        </>
      )}
    </div>
  );
}

function relativeSeen(at) {
  if (!at) return null;
  try {
    const d = new Date(at);
    if (!Number.isFinite(d.getTime())) return null;
    return formatDistanceToNow(d, { addSuffix: false });
  } catch {
    return null;
  }
}

/** Prefer augment.viewers[]; fall back to last_view_* for older payloads. */
function resolveViewers(rec) {
  if (Array.isArray(rec?.viewers) && rec.viewers.length > 0) {
    return rec.viewers
      .map((v) => ({
        userId: v.user_id ?? null,
        userName: v.user_name || 'Someone',
        lastViewedAt: v.last_viewed_at ?? null,
      }))
      .filter((v) => v.userName);
  }
  if (rec?.last_view_user_name || rec?.has_views) {
    return [
      {
        userId: rec.last_view_user_id ?? null,
        userName: rec.last_view_user_name || 'Someone',
        lastViewedAt: rec.last_view_at ?? null,
      },
    ];
  }
  return [];
}

function ViewersCell({ rec }) {
  const viewers = resolveViewers(rec);
  if (viewers.length === 0) return '—';

  const primary = viewers[0];
  const namedExtras = viewers.slice(1);
  // viewers[] is authoritative; otherwise fall back to viewer_count for "+N"
  const extraCount =
    namedExtras.length > 0
      ? namedExtras.length
      : Math.max(0, (Number(rec?.viewer_count) || viewers.length) - viewers.length);
  const primaryRel = relativeSeen(primary.lastViewedAt);
  const label = (
    <span>
      {primary.userName}
      {primaryRel ? ` ${primaryRel}` : ''}
      {extraCount > 0 ? ` · +${extraCount}` : ''}
    </span>
  );

  if (viewers.length <= 1 && extraCount === 0) return label;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-default border-b border-dotted border-gray-400">
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[260px] text-xs space-y-1">
          {viewers.map((v, i) => {
            const rel = relativeSeen(v.lastViewedAt);
            return (
              <p key={v.userId || `${v.userName}-${i}`}>
                {v.userName}
                {rel ? ` · ${rel}` : ''}
              </p>
            );
          })}
          {namedExtras.length === 0 && extraCount > 0 && (
            <p className="text-gray-400">+{extraCount} more</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function daysSince(at) {
  if (!at) return null;
  try {
    const d = new Date(at);
    if (!Number.isFinite(d.getTime())) return null;
    return Math.max(0, differenceInCalendarDays(new Date(), d));
  } catch {
    return null;
  }
}

function dealMatchesProfileCategories(deal, profile) {
  if (!profile) return true;
  const dealCats = getDealCategories(deal);
  if (dealCats.length === 0) return true;
  const profileCats = getProfileProcurementCategories(profile).map(
    (c) => toOfficialCategoryName(c) || String(c).trim()
  );
  if (profileCats.length === 0) return true;
  return dealCats.some((dealCat) => {
    const dealLower = String(dealCat || '').toLowerCase();
    return profileCats.some((profileCat) => {
      const profileLower = String(profileCat || '').toLowerCase();
      return (
        profileLower === dealLower ||
        dealLower.includes(profileLower) ||
        profileLower.includes(dealLower)
      );
    });
  });
}

function dealMatchesAnyActiveProfile(deal, profiles) {
  if (!profiles?.length) return dealMatchesProfileCategories(deal, null);
  return profiles.some((p) => dealMatchesProfileCategories(deal, p));
}

// Fit labels arrive as English enum values (also used for colours / sorting); translate only for display.
const FIT_LABEL_KEYS = {
  'Strong Fit': 'strongFit',
  'Moderate Fit': 'moderateFit',
  'Weak Fit': 'procWeakFit',
  'No Fit': 'procNoFit',
  'Insufficient Info': 'insufficientInfo',
  'Low Evidence': 'procLowEvidence',
};

function FitBadge({ deal, activeProfiles, fitOverride, t }) {
  const categoryMatch = dealMatchesAnyActiveProfile(deal, activeProfiles);
  if (!categoryMatch) {
    return (
      <span
        className="text-xs text-gray-500"
        title={t('procFitOutOfScopeTitle')}
      >
        {t('outOfScope')}
      </span>
    );
  }

  const loaded = fitOverride?.loaded ?? false;
  const jobStatus = fitOverride?.fit_job_status ?? deal?.fit_job_status ?? null;
  const outcome = fitOverride?.fit_light_outcome ?? deal?.fit_light_outcome;
  let label = fitOverride?.fit_light_label ?? deal?.fit_light_label;

  if (outcome === 'insufficient_information') {
    label = t('insufficientInfo');
  }

  if (label) {
    const colorMap = {
      'Strong Fit': 'bg-green-100 text-green-800 border-green-200',
      'Moderate Fit': 'bg-amber-100 text-amber-800 border-amber-200',
      'Weak Fit': 'bg-orange-100 text-orange-800 border-orange-200',
      'No Fit': 'bg-red-100 text-red-800 border-red-200',
      'Insufficient Info': 'bg-gray-100 text-gray-600 border-gray-200',
      'Low Evidence': 'bg-gray-100 text-gray-600 border-gray-200',
    };
    const colors = colorMap[label] || 'bg-gray-100 text-gray-600 border-gray-200';
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${colors}`}
      >
        {FIT_LABEL_KEYS[label] ? t(FIT_LABEL_KEYS[label]) : label}
      </span>
    );
  }

  if (jobStatus === 'queued' || jobStatus === 'processing') {
    return <span className="text-xs text-gray-400">{t('pending')}</span>;
  }

  if (activeProfiles?.length && !loaded && !deal?.fit_light_label) {
    return <span className="text-xs text-gray-400">{t('procLoading')}</span>;
  }

  return <span className="text-xs text-gray-400">—</span>;
}

function resolveFitOverride(fitOverrides, tenantId, deal) {
  if (!fitOverrides?.size) return null;
  const id = deal?.fit_deal_id ?? deal?.id;
  return fitOverrides.get(`${tenantId}:${id}`) ?? fitOverrides.get(id) ?? null;
}

function Sparkline({ months }) {
  if (!months?.length) return null;

  const max = Math.max(...months.map((m) => Number(m.count) || 0), 1);
  const barW = 14;
  const gap = 4;
  const height = 40;
  const width = months.length * (barW + gap);

  return (
    <div>
      <svg width={width} height={height} className="block" aria-hidden>
        {months.map((m, i) => {
          const count = Number(m.count) || 0;
          const h = Math.max(2, (count / max) * (height - 4));
          return (
            <rect
              key={m.month}
              x={i * (barW + gap)}
              y={height - h}
              width={barW}
              height={h}
              rx={2}
              fill="#00C2C2"
            />
          );
        })}
      </svg>
    </div>
  );
}

function getStatusSortValue(rec) {
  const status = rec?.status ?? null;
  if (status === 'reviewing') return 1;
  if (status === 'pursuing') return 2;
  if (status === 'submitted') return 3;
  if (status === 'won') return 4;
  if (status === 'lost') return 5;
  if (status === 'passed') return 6;
  if (!status) return 7;
  return 8;
}

function DealValueCell({ dealId, deal, augment, onValueOverrideChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const display = getDealValueDisplay(deal, augment);
  const formatted = formatDealValueDisplay(display);
  const nativeHint = formatNativeValueHint(display);
  const overrideTooltip =
    display.isOverride && (display.overrideBy || display.overrideAt)
      ? `${display.overrideBy || 'Someone'} · ${formatStatusUpdatedAt(display.overrideAt) || '—'}`
      : null;

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const startEdit = (e) => {
    e.stopPropagation();
    setDraft(display.value != null ? String(display.value) : '');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setDraft('');
  };

  const saveEdit = async () => {
    if (!onValueOverrideChange) return;
    const trimmed = draft.trim();
    if (!trimmed) {
      cancelEdit();
      return;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n) || n <= 0) {
      cancelEdit();
      return;
    }
    setSaving(true);
    try {
      await onValueOverrideChange(dealId, n);
      setEditing(false);
    } catch {
      // Parent shows toast and reverts augment.
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async (e) => {
    e.stopPropagation();
    if (!onValueOverrideChange) return;
    setSaving(true);
    try {
      await onValueOverrideChange(dealId, null);
    } catch {
      // Parent shows toast.
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div
        className="flex items-center gap-1 min-w-[120px]"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {display.currency ? (
          <span className="text-xs text-gray-500 shrink-0">{display.currency}</span>
        ) : null}
        <Input
          ref={inputRef}
          type="number"
          min="0"
          step="any"
          value={draft}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') saveEdit();
            if (e.key === 'Escape') cancelEdit();
          }}
          onBlur={saveEdit}
          className="h-7 text-xs w-24"
        />
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-1 whitespace-nowrap cursor-pointer group"
      onClick={startEdit}
      onMouseDown={(e) => e.stopPropagation()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') startEdit(e);
      }}
    >
      {/* The cell shows USD once a converted figure exists. The tender's own
          amount stays reachable on hover, so a rep quoting the notice back to a
          buyer still has the local figure. */}
      <span
        className={`text-xs ${display.isOverride ? 'italic text-[#0A1A2F]' : 'text-[#0A1A2F]'}`}
        title={nativeHint ? `Tender value: ${nativeHint}` : undefined}
      >
        {formatted}
      </span>
      {display.isOverride && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex shrink-0" onClick={(e) => e.stopPropagation()}>
                <Pencil className="w-3 h-3 text-gray-400" aria-hidden />
              </span>
            </TooltipTrigger>
            {overrideTooltip && (
              <TooltipContent side="top" className="text-xs max-w-[220px]">
                {overrideTooltip}
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      )}
      {display.isOverride && onValueOverrideChange && (
        <button
          type="button"
          className="p-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-gray-100 text-gray-400 hover:text-gray-600"
          aria-label="Clear value override"
          disabled={saving}
          onClick={handleClear}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function getFitSortScore(deal, fitOverrides, tenantId) {
  const override = resolveFitOverride(fitOverrides, tenantId, deal);
  const label = override?.fit_light_label ?? deal?.fit_light_label;
  if (FIT_NULL_LABELS.has(label)) return null;
  const score = Number(override?.fit_light_score ?? deal?.fit_light_score);
  return Number.isFinite(score) ? score : null;
}

function sortMyTendersRows(rows, sortConfig, augment, fitOverrides, tenantId) {
  const dir = sortConfig.direction === 'ascending' ? 1 : -1;
  const key = sortConfig.key;
  return [...rows].sort((a, b) => {
    const aId = resolveDealId(a);
    const bId = resolveDealId(b);
    const aRec = augment[aId] || {};
    const bRec = augment[bId] || {};

    let av;
    let bv;
    switch (key) {
      case 'status':
        av = getStatusSortValue(aRec);
        bv = getStatusSortValue(bRec);
        break;
      case 'title':
        av = (a.title || '').toLowerCase();
        bv = (b.title || '').toLowerCase();
        break;
      case 'institution':
        av = (a.institution || '').toLowerCase();
        bv = (b.institution || '').toLowerCase();
        break;
      case 'fit':
        av = getFitSortScore(a, fitOverrides, tenantId);
        bv = getFitSortScore(b, fitOverrides, tenantId);
        break;
      case 'deal_value': {
        const aVal =
          aRec.value_override ??
          a.deal_value_usd ??
          a.deal_value ??
          a.estimated_value ??
          a.value;
        const bVal =
          bRec.value_override ??
          b.deal_value_usd ??
          b.deal_value ??
          b.estimated_value ??
          b.value;
        av = Number.isFinite(Number(aVal)) ? Number(aVal) : null;
        bv = Number.isFinite(Number(bVal)) ? Number(bVal) : null;
        break;
      }
      case 'last_seen':
        av = aRec.last_view_at ? new Date(aRec.last_view_at).getTime() : 0;
        bv = bRec.last_view_at ? new Date(bRec.last_view_at).getTime() : 0;
        break;
      case 'last_updated':
        av = aRec.status_set_at ? new Date(aRec.status_set_at).getTime() : 0;
        bv = bRec.status_set_at ? new Date(bRec.status_set_at).getTime() : 0;
        break;
      case 'deadline': {
        const aHorizon = getDeadlineHorizon(a.deadline);
        const bHorizon = getDeadlineHorizon(b.deadline);
        av =
          aHorizon.days != null && aHorizon.days >= 0
            ? aHorizon.days
            : a.deadline
              ? new Date(a.deadline).getTime()
              : Number.MAX_SAFE_INTEGER;
        bv =
          bHorizon.days != null && bHorizon.days >= 0
            ? bHorizon.days
            : b.deadline
              ? new Date(b.deadline).getTime()
              : Number.MAX_SAFE_INTEGER;
        break;
      }
      default:
        av = 0;
        bv = 0;
    }

    if (key === 'fit' || key === 'deal_value') {
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
    }

    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return (a.title || '').localeCompare(b.title || '');
  });
}

function SortableHeader({ children, sortKey, sortConfig, setSortConfig, className }) {
  const isSorted = sortConfig.key === sortKey;
  const direction = isSorted ? sortConfig.direction : 'none';
  const handleSort = () => {
    setSortConfig({
      key: sortKey,
      direction: direction === 'ascending' ? 'descending' : 'ascending',
    });
  };
  return (
    <TableHead
      onClick={handleSort}
      className={`cursor-pointer hover:bg-gray-100 select-none ${className || ''}`}
    >
      <div className="flex items-center gap-1">
        {children}
        {isSorted &&
          (direction === 'ascending' ? (
            <ArrowUp className="w-3 h-3 shrink-0" />
          ) : (
            <ArrowDown className="w-3 h-3 shrink-0" />
          ))}
      </div>
    </TableHead>
  );
}

function TimelineRow({ events }) {
  if (!events?.length) {
    return <p className="text-xs text-gray-500 py-2 pl-8">No activity yet</p>;
  }
  return (
    <ul className="text-xs text-[#5C6B7A] space-y-1 py-2 pl-8">
      {events.map((ev, i) => {
        const at = ev.at ? format(new Date(ev.at), 'MMM d, yyyy h:mm a') : '—';
        if (ev.kind === 'view') {
          return (
            <li key={i}>
              <span className="text-[#00C2C2]">View</span> — {ev.user_name || 'Someone'} · {at}
            </li>
          );
        }
        if (ev.kind === 'status_change') {
          const from = ev.from_status || '—';
          const to = ev.to_status || '—';
          return (
            <li key={i}>
              <span className="text-[#00C2C2]">Status</span> {from} → {to}
              {ev.user_name ? ` · ${ev.user_name}` : ''} · {at}
              {ev.note ? <span className="block text-gray-500 mt-0.5">"{ev.note}"</span> : null}
            </li>
          );
        }
        return <li key={i}>{at}</li>;
      })}
    </ul>
  );
}

const MyTendersTab = forwardRef(function MyTendersTab(
  {
    tenantId,
    primaryCategories = [],
    liveDeals = [],
    recentlyExpiredDeals = [],
    dealAugmentByDealId = {},
    onStatusChange,
    onValueOverrideChange,
    onDealViewLog,
    activityUnavailable = false,
  },
  ref
) {
  const { t } = useTranslation();
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [showLive, setShowLive] = useState(true);
  const [showRecentlyExpired, setShowRecentlyExpired] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'fit', direction: 'descending' });
  const [expandedId, setExpandedId] = useState(null);
  const [timelineCache, setTimelineCache] = useState(() => new Map());
  const [timelineLoading, setTimelineLoading] = useState(null);
  const [activeProductProfiles, setActiveProductProfiles] = useState([]);
  const [fitOverrides, setFitOverrides] = useState(() => new Map());
  const fitOverridesRef = useRef(new Map());

  useEffect(() => {
    if (!tenantId) {
      setActiveProductProfiles([]);
      return;
    }
    fetchProductProfiles(tenantId)
      .then((list) => setActiveProductProfiles((list || []).filter((p) => p.is_active)))
      .catch(() => setActiveProductProfiles([]));
  }, [tenantId]);

  // v1: primaries-only — deals outside tenant primary categories are excluded.
  // TODO(v2): augment include_all_with_status=true to keep historical status rows visible.
  const primaryFilteredLive = useMemo(() => {
    if (!primaryCategories?.length) return [];
    return liveDeals.filter((d) =>
      dealMatchesPrimaryProcurementCategories(d, primaryCategories)
    );
  }, [liveDeals, primaryCategories]);

  const primaryFilteredRecently = useMemo(() => {
    if (!primaryCategories?.length) return [];
    return recentlyExpiredDeals.filter((d) =>
      dealMatchesPrimaryProcurementCategories(d, primaryCategories)
    );
  }, [recentlyExpiredDeals, primaryCategories]);

  const tableDeals = useMemo(() => {
    const rows = [];
    if (showLive) rows.push(...primaryFilteredLive);
    if (showRecentlyExpired) rows.push(...primaryFilteredRecently);
    const seen = new Set();
    return rows.filter((d) => {
      const key = resolveDealId(d);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [showLive, showRecentlyExpired, primaryFilteredLive, primaryFilteredRecently]);

  const normalizedRows = useMemo(
    () =>
      tableDeals
        .map((d) => normalizeDeal(d, d.account))
        .filter((d) => d && resolveDealId(d)),
    [tableDeals]
  );

  const sortedRows = useMemo(
    () => sortMyTendersRows(normalizedRows, sortConfig, dealAugmentByDealId, fitOverrides, tenantId),
    [normalizedRows, sortConfig, dealAugmentByDealId, fitOverrides, tenantId]
  );

  const fetchFitForDeals = useCallback(async () => {
    if (!tenantId || !activeProductProfiles?.length) return;

    const toFetch = normalizedRows.filter((d) => {
      if (!d?.fit_deal_id) return false;
      if (!dealMatchesAnyActiveProfile(d, activeProductProfiles)) return false;
      const key = `${tenantId}:${d.fit_deal_id}`;
      const override = fitOverridesRef.current.get(key);
      const rowHasSavedFit = !!(d.fit_light_label || override?.fit_light_label);
      const activeJob =
        override?.fit_job_status === 'queued' ||
        override?.fit_job_status === 'processing' ||
        d.fit_job_status === 'queued' ||
        d.fit_job_status === 'processing';
      if (activeJob) return true;
      if (rowHasSavedFit) return false;
      if (override?.loaded) return false;
      return true;
    });

    if (toFetch.length === 0) return;

    const queue = [...toFetch];
    const results = [];

    async function runWorker() {
      while (queue.length > 0) {
        const deal = queue.shift();
        if (!deal) break;
        const response = await fetchFitLatest(tenantId, deal.fit_deal_id).catch(() => null);
        if (response) {
          results.push({
            key: `${tenantId}:${deal.fit_deal_id}`,
            data: response,
          });
        }
      }
    }

    await Promise.all(Array.from({ length: FIT_CONCURRENCY }, runWorker));

    const next = new Map(fitOverridesRef.current);
    for (const result of results) {
      const light = result.data?.light || null;
      const lightJob = result.data?.light_job || null;
      next.set(result.key, {
        loaded: true,
        fit_light_label: light?.label ?? null,
        fit_light_score: light?.score ?? null,
        fit_light_outcome: light?.outcome ?? null,
        fit_job_status: lightJob?.status ?? null,
      });
    }
    fitOverridesRef.current = next;
    setFitOverrides(new Map(next));
  }, [tenantId, activeProductProfiles, normalizedRows]);

  const activeProfileIdsKey = (activeProductProfiles || []).map((p) => p.id).sort().join(',');

  useEffect(() => {
    fitOverridesRef.current = new Map();
    setFitOverrides(new Map());
  }, [tenantId, activeProfileIdsKey]);

  useEffect(() => {
    fetchFitForDeals();
    const interval = setInterval(fetchFitForDeals, FIT_POLL_MS);
    return () => clearInterval(interval);
  }, [fetchFitForDeals]);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    setSummaryLoading(true);
    fetchMyTendersSummary(tenantId, primaryCategories)
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((e) => {
        if (!cancelled) {
          console.warn('[MyTenders] summary failed', e);
          setSummary(null);
        }
      })
      .finally(() => {
        if (!cancelled) setSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, primaryCategories.join(',')]);

  const refetchSummary = useMemo(
    () =>
      debounce(() => {
        if (!tenantId) return;
        fetchMyTendersSummary(tenantId, primaryCategories)
          .then((data) => setSummary(data))
          .catch(() => {});
      }, 300),
    [tenantId, primaryCategories.join(',')]
  );

  useEffect(() => () => refetchSummary.cancel(), [refetchSummary]);

  useImperativeHandle(ref, () => ({ refetchSummary }), [refetchSummary]);

  const pursuingCount = useMemo(
    () =>
      countDealsWithStatuses(
        primaryFilteredLive,
        primaryFilteredRecently,
        dealAugmentByDealId,
        PURSUING_COUNTER_STATUSES
      ),
    [primaryFilteredLive, primaryFilteredRecently, dealAugmentByDealId]
  );

  const untouchedCount = useMemo(() => {
    return normalizedRows.filter((d) => {
      const id = resolveDealId(d);
      const rec = dealAugmentByDealId[id];
      return rec?.status == null;
    }).length;
  }, [normalizedRows, dealAugmentByDealId]);

  const untouchedStrongCount = useMemo(() => {
    return normalizedRows.filter((d) => {
      const id = resolveDealId(d);
      const rec = dealAugmentByDealId[id];
      if (rec?.status != null) return false;
      const override = resolveFitOverride(fitOverrides, tenantId, d);
      const fit = String(override?.fit_light_label ?? d.fit_light_label ?? '').toLowerCase();
      return fit === 'strong fit' || fit === 'strong';
    }).length;
  }, [normalizedRows, dealAugmentByDealId, fitOverrides, tenantId]);

  const sparklineMeta = useMemo(() => {
    const spark = summary?.sparkline || [];
    if (spark.length < 1) return { current: 0, delta: null };
    const current = Number(spark[spark.length - 1]?.count) || 0;
    const prev = spark.length > 1 ? Number(spark[spark.length - 2]?.count) || 0 : 0;
    const delta = spark.length > 1 ? current - prev : null;
    return { current, delta };
  }, [summary]);

  const toggleExpand = async (dealId) => {
    if (expandedId === dealId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(dealId);
    // Expanding shows the deal timeline / activity — count as a deal-detail open.
    if (dealId) onDealViewLog?.(dealId);
    if (timelineCache.has(dealId)) return;
    setTimelineLoading(dealId);
    try {
      const data = await fetchMyTendersTimeline(tenantId, dealId);
      setTimelineCache((prev) => new Map(prev).set(dealId, data?.timeline || []));
    } finally {
      setTimelineLoading(null);
    }
  };

  const counters = summary?.counters || {};
  const currency = summary?.pipeline_currency || 'GBP';
  const pipelineValued =
    (Number(counters.pipeline_count) || 0) - (Number(counters.pipeline_unknown_count) || 0);
  const pipelineTotal = Number(counters.pipeline_count) || 0;

  return (
    <div className="space-y-4">
      {activityUnavailable && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Deal activity unavailable — status and viewer badges may be incomplete. Try refreshing.
        </div>
      )}
      {/* Sparkline + deals generated header */}
      <div className="rounded-lg border bg-white p-4">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-[#0A1A2F]">Deals generated</p>
            {summaryLoading ? (
              <Skeleton className="h-4 w-48 mt-0.5" />
            ) : (
              <p className="text-xs text-[#5C6B7A] mt-0.5">
                {formatDealTrackingSinceLabel(summary?.tracking_started_at)}
              </p>
            )}
            {summaryLoading ? (
              <Skeleton className="h-10 w-48 mt-2" />
            ) : (
              <div className="mt-2">
                <Sparkline months={summary?.sparkline} />
              </div>
            )}
          </div>
          {!summaryLoading && summary?.sparkline?.length > 0 && (
            <p className="text-xs text-[#5C6B7A] whitespace-nowrap">
              last 6 months · this month: {sparklineMeta.current}
              {sparklineMeta.delta != null && (
                <span className={sparklineMeta.delta >= 0 ? ' text-green-600' : ' text-red-600'}>
                  {' '}
                  ({sparklineMeta.delta >= 0 ? '+' : ''}
                  {sparklineMeta.delta} vs last)
                </span>
              )}
            </p>
          )}
        </div>
      </div>

      {/* Counter strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {summaryLoading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))
        ) : (
          <>
            <div className="rounded-lg border bg-white p-3">
              <p className="text-xs text-[#5C6B7A]">Pursuing</p>
              <p className="text-2xl font-bold text-[#0A1A2F]">{pursuingCount}</p>
            </div>
            <div className="rounded-lg border bg-white p-3">
              <p className="text-xs text-[#5C6B7A]">Untouched</p>
              <p className="text-2xl font-bold text-[#0A1A2F]">{untouchedCount}</p>
              {untouchedStrongCount > 0 && (
                <p className="text-xs text-[#5C6B7A]">(Strong: {untouchedStrongCount})</p>
              )}
            </div>
            <TotalDealValueCard counters={counters} />
            <div className="rounded-lg border bg-white p-3">
              <p className="text-xs text-[#5C6B7A]">Pipeline</p>
              <p
                className="text-2xl font-bold text-[#0A1A2F]"
                title={`$${Math.round(Number(counters.pipeline_value) || 0).toLocaleString()}`}
              >
                {formatUSDCompact(counters.pipeline_value)}
              </p>
              {pipelineTotal > 0 && Number(counters.pipeline_unknown_count) > 0 && (
                <p className="text-xs text-[#5C6B7A]">
                  {pipelineValued} of {pipelineTotal} valued
                </p>
              )}
            </div>
            <div className="rounded-lg border bg-white p-3">
              <p className="text-xs text-[#5C6B7A]">Won</p>
              <p className="text-2xl font-bold text-[#0A1A2F]">{counters.won_total ?? 0}</p>
              {counters.won_value_total != null && (
                <p className="text-xs text-[#5C6B7A]">
                  ({formatMoney(counters.won_value_total, currency)})
                </p>
              )}
            </div>
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant={showLive ? 'default' : 'outline'}
          onClick={() => setShowLive((v) => !v)}
        >
          Live ({primaryFilteredLive.length})
        </Button>
        <Button
          type="button"
          size="sm"
          variant={showRecentlyExpired ? 'default' : 'outline'}
          onClick={() => setShowRecentlyExpired((v) => !v)}
        >
          {t('recentlyExpired')} ({primaryFilteredRecently.length})
        </Button>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-8" />
              <SortableHeader sortKey="status" sortConfig={sortConfig} setSortConfig={setSortConfig} className="w-28">
                Status
              </SortableHeader>
              <SortableHeader sortKey="title" sortConfig={sortConfig} setSortConfig={setSortConfig}>
                Deal title
              </SortableHeader>
              <SortableHeader sortKey="institution" sortConfig={sortConfig} setSortConfig={setSortConfig}>
                Buyer
              </SortableHeader>
              <SortableHeader sortKey="fit" sortConfig={sortConfig} setSortConfig={setSortConfig}>
                Fit
              </SortableHeader>
              <SortableHeader sortKey="deal_value" sortConfig={sortConfig} setSortConfig={setSortConfig}>
                Deal Value
              </SortableHeader>
              <SortableHeader sortKey="last_seen" sortConfig={sortConfig} setSortConfig={setSortConfig}>
                Last seen
              </SortableHeader>
              <SortableHeader sortKey="last_updated" sortConfig={sortConfig} setSortConfig={setSortConfig}>
                Last updated
              </SortableHeader>
              <SortableHeader sortKey="deadline" sortConfig={sortConfig} setSortConfig={setSortConfig}>
                Deadline
              </SortableHeader>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-gray-500">
                  {!showLive && !showRecentlyExpired
                    ? 'Select Live and/or Recently expired to show deals'
                    : primaryCategories?.length
                      ? 'No deals in your primary categories'
                      : 'No deals match the current filters'}
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((deal) => {
                const dealId = resolveDealId(deal);
                const rec = dealAugmentByDealId[dealId] || {};
                const horizon = getDeadlineHorizon(deal.deadline);
                const daysToDeadline =
                  horizon.days != null && horizon.days >= 0 ? horizon.days : null;
                const noRecentActivity = !rec.has_views && !rec.last_view_at;
                const statusNull = rec.status == null;
                const daysSinceActivity = rec.last_view_at
                  ? daysSince(rec.last_view_at)
                  : daysSince(deal.published_at || deal.created_at);
                const accountabilityWarn =
                  (statusNull || noRecentActivity) &&
                  daysSinceActivity != null &&
                  (daysToDeadline != null
                    ? daysSinceActivity > Math.max(14, daysToDeadline - 7)
                    : daysSinceActivity > 14);

                const statusUpdatedLabel = formatStatusUpdatedAt(rec.status_set_at);

                const isClosed = String(deal.status || '').toLowerCase() !== 'live';
                const fitOverride = resolveFitOverride(fitOverrides, tenantId, deal);
                const deadlineUrgent =
                  !isClosed && horizon.days != null && horizon.days <= 7;
                const showDeadlineWarn = deadlineUrgent || accountabilityWarn;

                return (
                  <React.Fragment key={dealId}>
                    <TableRow className="hover:bg-gray-50">
                      <TableCell className="w-8 p-2">
                        <button
                          type="button"
                          className="p-1 rounded hover:bg-gray-100"
                          onClick={() => toggleExpand(dealId)}
                          aria-label="Toggle timeline"
                        >
                          {expandedId === dealId ? (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500" />
                          )}
                        </button>
                      </TableCell>
                      <TableCell>
                        <DealStatusPicker
                          dealId={dealId}
                          augment={rec}
                          variant="badge"
                          onStatusChange={onStatusChange}
                        />
                      </TableCell>
                      <TableCell className="max-w-[220px]">
                        <div className="flex items-center gap-1 min-w-0 font-medium">
                          {deal.source_url ? (
                            <a
                              href={deal.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="truncate text-[#0A1A2F] hover:text-[#00C2C2] transition-colors"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {deal.title || '—'}
                            </a>
                          ) : (
                            <span className="truncate">{deal.title || '—'}</span>
                          )}
                          {deal.source_url && (
                            <SourceLink
                              url={deal.source_url}
                              variant="icon"
                              className="shrink-0"
                              onClick={(e) => e.stopPropagation()}
                            />
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-[140px] truncate">
                        {deal.institution || '—'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <FitBadge
                          deal={deal}
                          activeProfiles={activeProductProfiles}
                          fitOverride={fitOverride}
                          t={t}
                        />
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <DealValueCell
                          dealId={dealId}
                          deal={deal}
                          augment={rec}
                          onValueOverrideChange={onValueOverrideChange}
                        />
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        <ViewersCell rec={rec} />
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {rec.status_set_at ? (
                          <span>
                            {rec.status_set_by_user_name ? `${rec.status_set_by_user_name} · ` : ''}
                            {statusUpdatedLabel || '—'}
                          </span>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell className="text-xs whitespace-nowrap">
                        {isClosed ? (
                          <span className="text-gray-500">— Closed</span>
                        ) : deal.deadline ? (
                          <span className={showDeadlineWarn ? 'text-amber-600 font-medium' : ''}>
                            in {horizon.days != null ? `${horizon.days}d` : horizon.label}
                            {showDeadlineWarn && (
                              <AlertTriangle className="w-3 h-3 inline ml-0.5 text-amber-500" />
                            )}
                          </span>
                        ) : (
                          horizon.label
                        )}
                      </TableCell>
                    </TableRow>
                    {expandedId === dealId && (
                      <TableRow className="bg-gray-50/80">
                        <TableCell colSpan={9} className="py-0">
                          {timelineLoading === dealId ? (
                            <p className="text-xs text-gray-500 py-2 pl-8">Loading timeline…</p>
                          ) : (
                            <TimelineRow events={timelineCache.get(dealId)} />
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
});

export default MyTendersTab;
